import {
  BaseQueryApi,
  FetchArgs,
  createApi,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query/react";
import {
  logOut,
  readStoredAuth,
  setCredentials,
} from "./reducers/auth/authSlice";
import { RootState } from "./rootStore";

/**
 * Every request ends within 20 s, like the station app (`packages/mobile/api/requestTimeout.ts`).
 * A timed-out request resolves with `error.status === "TIMEOUT_ERROR"`.
 */
export const REQUEST_TIMEOUT_MS = 20_000;

/**
 * For requests that legitimately run long: file uploads and work the server does inside the
 * request (a roster import or vendor sync, a session's certificate PDFs). Matches the station
 * app's upload limit. Set per request as `timeout` in the endpoint's FetchArgs.
 */
export const LONG_REQUEST_TIMEOUT_MS = 5 * 60_000;

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  timeout: REQUEST_TIMEOUT_MS,
  prepareHeaders: (headers, { getState }) => {
    const accessToken =
      (getState() as RootState).auth.accessToken ??
      readStoredAuth().accessToken;
    if (accessToken) headers.set("authorization", `Bearer ${accessToken}`);
    return headers;
  },
});

/**
 * Refresh-and-retry on 401 once. Only a refresh the server refuses (4xx) signs the user out; a
 * refresh that times out or cannot reach the server leaves the session alone and returns the 401.
 */
/** An HTTP 4xx answer, as opposed to a timeout or network error (whose status is a string). */
const isRefusal = (status: unknown) =>
  typeof status === "number" && status >= 400 && status < 500;

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: object,
) => {
  let result = await baseQuery(args, api, extraOptions);

  // Admins without a second factor are routed to enrollment (constitution Principle III, FR-035).
  if (
    result?.error?.status === 403 &&
    (result.error.data as any)?.message === "MFA_REQUIRED" &&
    !window.location.pathname.startsWith("/grading/mfa")
  ) {
    window.location.assign("/grading/mfa");
    return result;
  }
  if (result?.error?.status !== 401) return result;

  const refreshToken =
    (api.getState() as RootState).auth.refreshToken ??
    readStoredAuth().refreshToken;
  if (!refreshToken) return result;

  const refreshResult = await baseQuery(
    { url: "/api/account/refresh", method: "POST", body: { refreshToken } },
    api,
    extraOptions,
  );
  const refreshData = refreshResult?.data as
    | { success: boolean; data: { accessToken: string; refreshToken: string } }
    | undefined;
  if (refreshData?.success) {
    api.dispatch(setCredentials(refreshData.data));
    result = await baseQuery(args, api, extraOptions);
  } else if (isRefusal(refreshResult?.error?.status)) {
    api.dispatch(logOut());
  }
  return result;
};

export const apiSlice = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: [
    "Profile",
    "Staff",
    "Invitations",
    "Classes",
    "Students",
    "Sessions",
    "Session",
    "Reports",
    "Documents",
    "Benchmarks",
    "Districts",
    "DistrictHistory",
    "Schools",
    "RosterSyncs",
    "RosterStatus",
    "RosterCredentials",
    "RosterRejected",
    "DataRequests",
    "Privacy",
  ],
});
